/**
 * PerformanceBarView - Displays a single performance bar or DNP box
 *
 * Renders a bar chart element with optional hover tooltip and game number label.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

import type { MatchStatus } from "@/features/player-detail/types";

/**
 * Performance data with formatted date for display
 */
interface DisplayPerformance {
  readonly id: string;
  readonly matchDate: string;
  readonly formattedDate: string;
  readonly opponent: string;
  readonly status: MatchStatus;
  readonly rating: number;
  readonly minutes: number;
}

/**
 * Props for PerformanceBarView component
 */
interface PerformanceBarViewProps {
  readonly perf: DisplayPerformance;
  readonly isHovered: boolean;
  /** Whether any bar in the chart is currently hovered/selected */
  readonly isAnyBarHovered: boolean;
  readonly isDark: boolean;
  /** Whether the screen is mobile sized — shows persistent rating labels */
  readonly isSmallScreen: boolean;
  /** Pre-composed testID for E2E targeting, e.g. "PLAYER_DETAIL.PERFORMANCE_BAR-0" */
  readonly testID?: string;
  readonly onPointerEnter?: () => void;
  readonly onPointerLeave?: () => void;
}

/**
 * Get bar color based on match status
 * @param status - Match status (started, sub, dnp)
 */
function getBarColor(status: MatchStatus): string {
  const colors: Record<MatchStatus, string> = {
    started: "bg-status-success",
    sub: "bg-status-info",
    dnp: "bg-transparent",
  };
  return colors[status];
}

/**
 * Calculate bar height as percentage based on rating (0-10 scale)
 * @param rating - Rating value between 0-10
 */
function getBarHeightPercent(rating: number): number {
  return Math.max(5, (rating / 10) * 100);
}

/**
 * Rotated "Did Not Play" label style.
 * design-system: dynamic-style waiver — off-token rotated label geometry (60px)
 *
 * Uses a CSS transform string (not an RN transform array) because this rides the
 * web atom's UNSAFE_style (React.CSSProperties); an array would silently no-op on web.
 * RN accepts the string form too, so runtime behavior is preserved on both platforms.
 */
const DNP_LABEL_STYLE = {
  transform: "rotate(-90deg)",
  width: 60,
};

/**
 * Bar fill style scaled to the rating (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (rating-derived bar height)
 * @param rating - Rating value between 0-10
 */
const barHeightStyle = (rating: number) => ({
  height: `${getBarHeightPercent(rating)}%`,
});

/**
 * PerformanceBarView renders a single performance bar or DNP box
 * @param props - View props
 * @param props.perf - Performance data with rating, status, and match info
 * @param props.isHovered - Whether bar is currently hovered
 * @param props.isAnyBarHovered - Whether any bar in the chart is hovered
 * @param props.isDark - Whether dark theme is active
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.testID - Pre-composed testID (index-qualified)
 * @param props.onPointerEnter - Callback when pointer enters bar area (desktop only)
 * @param props.onPointerLeave - Callback when pointer leaves bar area (desktop only)
 * @returns VStack containing bar visualization
 */
const PerformanceBarView = ({
  perf,
  isHovered,
  isAnyBarHovered,
  isDark,
  isSmallScreen,
  testID,
  onPointerEnter,
  onPointerLeave,
}: PerformanceBarViewProps) => (
  <Stack
    {...{ testID, "data-testid": testID }}
    className={`relative h-full flex-1 items-center ${isAnyBarHovered && !isHovered ? "opacity-40" : ""}`}
    onPointerEnter={onPointerEnter}
    onPointerLeave={onPointerLeave}
  >
    {/* Mobile: persistent rating label above bar */}
    {isSmallScreen && perf.status !== "dnp" && (
      <Box className="pb-micro">
        <Text variant="micro-strong" className="text-accent-primary">
          {perf.rating.toFixed(1)}
        </Text>
      </Box>
    )}

    {/* Bar or DNP box */}
    <Box className="relative w-full max-w-8 flex-1 items-center justify-end">
      {perf.status === "dnp" ? (
        <Box
          className={`size-full items-center justify-center rounded-none border-2 border-dashed ${isDark ? "border-content-muted" : "border-content-subtle"}`}
        >
          <Box
            // eslint-disable-next-line no-restricted-syntax -- rotated DNP label geometry (transform rotate -90deg + off-ladder 60px width); not tokenizable
            UNSAFE_style={DNP_LABEL_STYLE}
          >
            <Text
              variant="micro"
              className={`text-center ${isDark ? "text-content-muted" : "text-content-subtle"}`}
            >
              Did Not Play
            </Text>
          </Box>
        </Box>
      ) : (
        <Box
          className={`w-full rounded-t-none ${getBarColor(perf.status)}`}
          // eslint-disable-next-line no-restricted-syntax -- data-driven bar height (rating-derived %); not tokenizable
          UNSAFE_style={barHeightStyle(perf.rating)}
        />
      )}
    </Box>
  </Stack>
);

PerformanceBarView.displayName = "PerformanceBarView";

export default memo(PerformanceBarView);
